import { z } from 'zod';
import { perfilUsuarioSchema } from '../autenticacao/tipos.js';
import type { PerfilUsuario } from '../autenticacao/tipos.js';
import { montarCaminho, requisitar } from './client.js';
import type { Pagina, ParametrosDePagina } from './paginacao.js';
import { paginaSchema, parametrosDePagina } from './paginacao.js';
import { analisar } from './validacao.js';

/**
 * Usuários do painel (UC13, RF-077/RF-078).
 *
 * Usuário do painel é pessoa que abre o navegador; operador do app é outra
 * entidade, em `operadores.ts`. Ninguém é apagado — a trilha de auditoria
 * referencia o autor —, só desativado.
 */

const CAMINHO_COLECAO = montarCaminho('/usuarios');

export const usuarioDoPainelSchema = z.object({
  id: z.string(),
  usuario: z.string(),
  nome: z.string(),
  perfil: perfilUsuarioSchema,
  ativo: z.boolean(),
  ultimoAcessoEm: z.string().nullable(),
  criadoEm: z.string(),
  alteradoEm: z.string(),
});

export type UsuarioDoPainel = z.infer<typeof usuarioDoPainelSchema>;

const paginaDeUsuariosSchema = paginaSchema(usuarioDoPainelSchema);

const alteracaoDeSenhaSchema = z.object({ sessoesEncerradas: z.number().int().nonnegative() });

export type AlteracaoDeSenha = z.infer<typeof alteracaoDeSenhaSchema>;

export type FiltroDeUsuarios = ParametrosDePagina & {
  /** Nome ou login. */
  readonly busca?: string;
  readonly perfil?: PerfilUsuario;
  readonly ativo?: boolean;
};

export type CadastroDeUsuario = {
  readonly usuario: string;
  readonly nome: string;
  readonly senha: string;
  readonly perfil: PerfilUsuario;
};

export type AlteracaoDeUsuario = {
  readonly nome?: string;
  readonly perfil?: PerfilUsuario;
  readonly ativo?: boolean;
};

export type NovaSenha = {
  /** Obrigatória quando o alvo é o próprio usuário autenticado. */
  readonly senhaAtual?: string;
  readonly novaSenha: string;
};

export async function listarUsuarios(
  filtro: FiltroDeUsuarios = {},
): Promise<Pagina<UsuarioDoPainel>> {
  const corpo = await requisitar(CAMINHO_COLECAO, {
    consulta: {
      ...parametrosDePagina(filtro),
      busca: filtro.busca,
      perfil: filtro.perfil,
      ativo: filtro.ativo,
    },
  });

  return analisar(paginaDeUsuariosSchema, corpo, CAMINHO_COLECAO);
}

export async function obterUsuario(id: string): Promise<UsuarioDoPainel> {
  const caminho = montarCaminho('/usuarios/{id}', { id });
  return analisar(usuarioDoPainelSchema, await requisitar(caminho), caminho);
}

/** A senha vai só no corpo — nunca na URL, que vai parar em log de proxy. */
export async function criarUsuario(cadastro: CadastroDeUsuario): Promise<UsuarioDoPainel> {
  const corpo = await requisitar(CAMINHO_COLECAO, { metodo: 'POST', corpo: cadastro });
  return analisar(usuarioDoPainelSchema, corpo, CAMINHO_COLECAO);
}

/** PATCH com só o que mudou: quem não mandou o campo não o está zerando. */
export async function atualizarUsuario(
  id: string,
  mudancas: AlteracaoDeUsuario,
): Promise<UsuarioDoPainel> {
  const caminho = montarCaminho('/usuarios/{id}', { id });
  const corpo = await requisitar(caminho, { metodo: 'PATCH', corpo: mudancas });
  return analisar(usuarioDoPainelSchema, corpo, caminho);
}

/**
 * Define a senha de um usuário.
 *
 * A própria senha exige `senhaAtual`; o Administrador redefine a de terceiros
 * sem ela. Nos dois casos as demais sessões do usuário afetado morrem na hora,
 * e a resposta diz quantas — a tela avisa quem clicou.
 */
export async function alterarSenhaDeUsuario(
  id: string,
  dados: NovaSenha,
): Promise<AlteracaoDeSenha> {
  const caminho = montarCaminho('/usuarios/{id}/senha', { id });
  const corpo = await requisitar(caminho, { metodo: 'POST', corpo: dados });
  return analisar(alteracaoDeSenhaSchema, corpo, caminho);
}
